import { useCallback, useEffect, useMemo, useRef, useState } from "react";

//components
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import CardHeader from "components/UI/Card/CardHeader";
import CardIcon from "components/UI/Card/CardIcon";
import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";

// import ButtonTS from "components/CustomButtons/ButtonTS";

// import { Tooltip } from "@mui/material";

// material-ui icons
import {
  PersonAddDisabled,
  PermIdentity,
  ChevronRight,
  ExpandMore,
  Sync,
  PersonAdd,
} from "@material-ui/icons";

import Skeleton from "react-loading-skeleton";

import { useTranslation } from "react-i18next";
// import { useGetSuppliers } from "services/input-core/suppliers/get-suppliers-paginated";

import { getBadgeFromStatus, getFilters, getSorting } from "utils/functions";
import { ColumnDef } from "@tanstack/react-table";
// import { Link } from "react-router-dom";
import { TitlePages } from "components/UI/TitlePage/TitlePages";
// import FieldglassButton from "components/FieldglassButton";
import { useGetUsers } from "services/input-core/users/get-users-paginated";
import useHasRole from "hooks/useHasRole";
import { EUsersStatus, RolesEnum } from "utils/@types";
import CustomPopover, {
  CustomPopoverRef,
} from "components/UI/CustomPopover/CustomPopover";
import { ButtonText } from "./components/styles";
import ConfirmDisabledModal from "components/Modals/ConfirmDisabledUserModal";
import ManualPaginatedTableExpanded from "components/Tables/ReactTable/ManualPaginatedTableExpanded";
import { useGetUsersSupplier } from "services/input-core/users/get-users-suppliers-paginated";
import { IUserRequest } from "services/input-core/users/@types";
import { useDisabledUsers } from "services/input-core/users/disabled-users";
import { toast } from "react-toastify";
import { useSubscription } from "store/auth/auth-store";
import { format, isValid, parse, parseISO } from "date-fns";

type ModalsState = {
  userDisabled: {
    open: boolean;
    userName: string;
    userID: string;
    userStatus: boolean;
  };
};

const UsersList = () => {
  const { t } = useTranslation("user-list");

  const { hasResourceRole, hasRealmRole } = useHasRole();

  const { subscription } = useSubscription();

  const userBuyer = hasResourceRole([RolesEnum.BUYER]);
  const userPrimaryContact = hasResourceRole([
    RolesEnum.SUPPLIER_PRIMARY_CONTACT,
  ]);
  const usersManager = hasRealmRole([RolesEnum.USERS_MANAGER]);

  const [dataTable, setDataTable] = useState<IUserRequest[]>();

  const [tableData, setTableData] = useState({
    pageSize: 20,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const [rowsData, setRowsData] = useState({
    pageSize: 100,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
    supplierCode: "",
    getRows: false,
  });

  const getFilterTypeUser = useMemo(() => {
    return {
      "filter[userType]":
        userBuyer && usersManager
          ? [RolesEnum.BUYER, RolesEnum.SUPPLIER_PRIMARY_CONTACT]
          : userBuyer
          ? [RolesEnum.SUPPLIER_PRIMARY_CONTACT]
          : userPrimaryContact && [RolesEnum.SUPPLIER],
    };
  }, [subscription]);

  const { data, isLoading, isFetching, refetch: refetchUsers } = useGetUsers({
    "page[size]": tableData.pageSize,
    "page[index]": tableData.pageIndex,
    ...getFilterTypeUser,
    ...getFilters(tableData.columnFilters),
    ...getSorting(tableData.sorting),
  });

  useEffect(() => {
    !isFetching &&
      setDataTable(() => {
        return data?.records?.map((user) => {
          return {
            ...user,
            subRows: [] as any[],
          };
        });
      });
  }, [data?.records, isFetching]);

  const usersSupplier = useGetUsersSupplier(
    rowsData?.getRows,
    rowsData?.supplierCode,
    {
      "page[size]": rowsData?.pageSize,
      "page[index]": rowsData?.pageIndex,
      ...getFilters(rowsData?.columnFilters),
      ...getSorting(rowsData?.sorting),
    },
    {
      staleTime: 0,
      onSuccess: (data: any) => {
        if (
          rowsData?.getRows &&
          (data?.records?.length <= 0 || Object.keys(data).length == 0)
        ) {
          toast.warning(t("info-sync-not-users"));
        } else {
          rowsData?.getRows && toast.success(t("success-sync-users"));
        }
      },
    },
  );

  const getUsersSupplier = useCallback((code: string) => {
    setRowsData((values) => ({
      ...values,
      supplierCode: code,
      getRows: true,
    }));
  }, []);

  useEffect(() => {
    !usersSupplier.isFetching &&
      setDataTable((values) => {
        return values?.map((user) => {
          if (user?.attributes[0]?.supplierCode[0] === rowsData?.supplierCode) {
            return { ...user, subRows: usersSupplier?.data?.records };
          } else {
            return {
              ...user,
            };
          }
        });
      });
  }, [usersSupplier?.data?.records, usersSupplier.isFetching]);

  const {
    mutate: disabledUser,
    // isLoading: isDisabledUserLoading,
  } = useDisabledUsers();

  const [modals, setModals] = useState<ModalsState>({
    userDisabled: {
      open: false,
      userName: "",
      userID: "",
      userStatus: false,
      // activityItem: undefined,
    },
  });

  const closeModal = (modalType: keyof ModalsState) => {
    setModals((prevModals) => ({
      ...prevModals,
      [modalType]: {
        ...prevModals[modalType],
        open: false,
      },
    }));
  };

  const openModal = (modalType: string, data: any) => {
    setModals({
      ...modals,
      [modalType]: {
        open: true,
        ...data,
      },
    });
  };

  const customPopoverRef = useRef<CustomPopoverRef>(null);

  const closePopoverFromParent = () => {
    if (customPopoverRef.current) {
      customPopoverRef.current.closePopover();
    }
  };

  const columns: ColumnDef<any, any>[] = [
    {
      header: t("user-table-status"),
      footer: (props: any) => props.column.id,
      accessorKey: "status",
      cell: ({ row, getValue }) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            paddingLeft: `${row.depth * 2}rem`,
          }}
        >
          {row.getCanExpand() &&
          row?.original?.userType === RolesEnum.SUPPLIER_PRIMARY_CONTACT ? (
            <button
              onClick={row.getToggleExpandedHandler()}
              style={{
                cursor: "pointer",
                border: "none",
                background: "transparent",
              }}
              disabled={usersSupplier?.isLoading}
            >
              {row.getIsExpanded() ? (
                <ExpandMore style={{ fontSize: "1.5rem" }} />
              ) : (
                <ChevronRight style={{ fontSize: "1.5rem" }} />
              )}
            </button>
          ) : !userBuyer ? (
            <span style={{ width: "1.5rem" }} />
          ) : row.depth >= 0 &&
            row?.original?.userType !== RolesEnum.SUPPLIER_PRIMARY_CONTACT ? (
            <span
              style={{
                width: row.depth == 0 ? "2.5rem" : "1.5rem",
              }}
            />
          ) : (
            <button
              style={{
                border: "none",
                cursor: "pointer",
                background: "transparent",
              }}
              onClick={() => {
                toast.info(t("info-sync-users"));
                rowsData?.supplierCode ===
                row?.original?.attributes[0]?.supplierCode[0]
                  ? usersSupplier.refetch()
                  : getUsersSupplier(
                      row?.original?.attributes[0]?.supplierCode[0],
                    );
              }}
              disabled={usersSupplier?.isLoading}
            >
              <Sync style={{ fontSize: "1.5rem" }} />
            </button>
          )}{" "}
          {getBadgeFromStatus(getValue(), (text: string) => t(text))}
        </div>
      ),
    },
    {
      header: t("user-table-type"),
      footer: (props: any) => props.column.id,
      accessorKey: "userType",
      cell: (info: any) =>
        getBadgeFromStatus(info.getValue(), (text: string) => t(text)),
    },
    {
      header: t("user-table-name"),
      footer: (props: any) => props.column.id,
      accessorKey: "name",
    },
    {
      header: t("user-table-email"),
      footer: (props: any) => props.column.id,
      accessorKey: "email",
    },
    {
      header: t("user-table-created-at"),
      footer: (props: any) => props.column.id,
      accessorKey: "createdAt",
      enableColumnFilter: false,
      cell: (info: any) => {
        const dateValue = info?.getValue()?.includes("T")
          ? parseISO(info?.getValue())
          : parse(info?.getValue(), "dd/MM/yyyy", new Date());
        return isValid(dateValue) ? format(dateValue, "dd/MM/yyyy") : "-";
      },
    },
    {
      header: t("user-table-action"),
      accessorKey: "actions",
      cell: ({ row }: any) => {
        return (
          <div
            style={{
              minHeight: "3rem",
            }}
          >
            {usersManager && (
              <CustomPopover
                identifier={`user-${row.id}`}
                ref={customPopoverRef}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    padding: "10px",
                  }}
                >
                  <ButtonText
                    onClick={() => {
                      openModal("userDisabled", {
                        open: true,
                        userName: row.original.name,
                        userID: row.original.userId,
                        userStatus:
                          row.original.status === EUsersStatus.INACTIVE,
                      });
                      closePopoverFromParent();
                    }}
                  >
                    {row.original.status === EUsersStatus.INACTIVE ? (
                      <PersonAdd style={{ fontSize: "1.2rem" }} />
                    ) : (
                      <PersonAddDisabled style={{ fontSize: "1.2rem" }} />
                    )}

                    {row.original.status === EUsersStatus.INACTIVE
                      ? t("user-enable")
                      : t("user-disable")}
                  </ButtonText>
                </div>
              </CustomPopover>
            )}
          </div>
        );
      },
      enableColumnFilter: false,
    },
  ];

  return (
    <>
      {modals.userDisabled.open && (
        <ConfirmDisabledModal
          userName={modals.userDisabled.userName}
          userStatus={modals.userDisabled.userStatus}
          modalOpen={modals.userDisabled.open}
          isDeleting={false}
          onClose={() => {
            closeModal("userDisabled");
          }}
          onConfirm={() => {
            disabledUser(
              {
                userId: modals.userDisabled.userID,
                activate: modals.userDisabled.userStatus,
              },
              {
                onSuccess: () => {
                  toast.success(
                    t(
                      !modals.userDisabled.userStatus
                        ? "success-disable-user"
                        : "success-enable-user",
                    ),
                  );
                  refetchUsers();
                },
                onError: (error) => {
                  console.log("[error] =>", error);
                  toast.error(t("err-user"));
                },
              },
            );
            closeModal("userDisabled");
          }}
        />
      )}
      <GridContainer>
        <GridItemTS key="user-list-data" xs={12}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <PermIdentity />
              </CardIcon>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <TitlePages type="sidebar" title="users" />
                {/* <FieldglassButton type="supplier" /> */}
              </div>
            </CardHeader>

            <CardBody>
              {isLoading || isFetching ? (
                <Skeleton />
              ) : (
                <ManualPaginatedTableExpanded
                  columns={columns}
                  data={dataTable || []}
                  tableState={tableData}
                  pageCount={data?.page_count}
                  setTableState={setTableData}
                  // fixedPageSize
                />
                // <p>Fim da busca</p>
              )}
            </CardBody>
          </Card>
        </GridItemTS>
      </GridContainer>
    </>
  );
};

export default UsersList;
